import { addDoc, collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { LearningEntry } from '@/types/learning';
import { trackPreferenceMetric } from '@/lib/preferencesAnalytics';
import type { ClientPreferences } from '@/types/preferences';

export type LearningEntryInput = Omit<LearningEntry, 'entryId' | 'createdAt'> & {
  entryId?: string;
  createdAt?: string;
};

const TAG_RULES: Array<{ tag: string; pattern: RegExp }> = [
  { tag: 'headline', pattern: /\bheadline\b/i },
  { tag: 'dateline', pattern: /\bdateline\b/i },
  { tag: 'quote', pattern: /\bquote\b/i },
  { tag: 'boilerplate', pattern: /\bboilerplate\b/i },
  { tag: 'tone', pattern: /\btone\b|\bvoice\b/i },
  { tag: 'style', pattern: /\bstyle\b|\bap style\b/i },
  { tag: 'media_contact', pattern: /\bmedia contact\b|\bpress contact\b/i },
];

const inferTags = (input: { userFeedback?: string; beforeText?: string; afterText?: string }): string[] => {
  const haystack = [input.userFeedback, input.beforeText, input.afterText].filter(Boolean).join('\n');
  if (!haystack) return [];
  const matches = TAG_RULES.filter((rule) => rule.pattern.test(haystack)).map((rule) => rule.tag);
  return Array.from(new Set(matches)).slice(0, 6);
};

export async function createLearningEntry(input: LearningEntryInput): Promise<LearningEntry | null> {
  if (!input.clientId || !input.beforeText || !input.afterText || !input.userFeedback) return null;
  try {
    const entryRef = input.entryId
      ? doc(db, 'clientLearning', input.clientId, 'entries', input.entryId)
      : doc(collection(db, 'clientLearning', input.clientId, 'entries'));
    const entryId = entryRef.id;
    const now = input.createdAt || new Date().toISOString();
    const tags = Array.isArray(input.tags) && input.tags.length ? input.tags : inferTags(input);

    const entry: LearningEntry = {
      clientId: input.clientId,
      entryId,
      runId: input.runId,
      taskType: input.taskType,
      beforeText: input.beforeText,
      afterText: input.afterText,
      userFeedback: input.userFeedback,
      tags,
      locale: input.locale,
      createdAt: now,
      createdBy: input.createdBy,
      source: input.source || 'chat',
    };

    await setDoc(entryRef, entry);
    trackPreferenceMetric('client_learning_entry_created_count', {
      clientId: input.clientId,
      entryId,
    });
    console.log('[learning] entry_created', { clientId: input.clientId, entryId });
    return entry;
  } catch (error) {
    console.warn('[learning] entry_create_failed', { clientId: input.clientId, error: (error as any)?.message || error });
    return null;
  }
}

export type PreferenceEventInput = {
  userId: string;
  clientId: string;
  signalType: 'explicit' | 'inferred';
  updates: Partial<ClientPreferences>;
  confidence?: number;
  runId?: string;
  messageId?: string;
  source?: string;
};

const DEFAULT_PREFERENCE_DIFF_MIN_CHARS = 40;

export async function createPreferenceEvent(input: PreferenceEventInput): Promise<boolean> {
  if (!input.userId || !input.clientId || !input.updates || !Object.keys(input.updates).length) return false;
  try {
    const eventsRef = collection(db, 'users', input.userId, 'clientPreferences', input.clientId, 'events');
    await addDoc(eventsRef, {
      type: 'preference_update',
      signalType: input.signalType,
      explicit: input.signalType === 'explicit',
      updates: input.updates,
      confidence: input.signalType === 'explicit' ? 1 : (input.confidence ?? 0.8),
      source: input.source || (input.signalType === 'explicit' ? 'ui_explicit' : 'inferred'),
      runId: input.runId || null,
      messageId: input.messageId || null,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.warn('[learning] preference_event_create_failed', {
      clientId: input.clientId,
      error: (error as any)?.message || error,
    });
    return false;
  }
}

export async function syncPreferenceEventToGraph(input: PreferenceEventInput, idToken?: string | null): Promise<boolean> {
  try {
    const response = await fetch('/api/graph/feedback/preferences', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({
        clientId: input.clientId,
        signalType: input.signalType,
        updates: input.updates,
        confidence: input.signalType === 'explicit' ? 1 : (input.confidence ?? 0.8),
        runId: input.runId,
        messageId: input.messageId,
        source: input.source,
      }),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      console.warn('[learning] preference_event_graph_sync_failed', {
        status: response.status,
        error: text,
      });
      return false;
    }
    return true;
  } catch (error) {
    console.warn('[learning] preference_event_graph_sync_error', (error as any)?.message || error);
    return false;
  }
}

export type PreferenceDiffPayload = {
  beforeText: string;
  afterText: string;
  diffChars: number;
  minDiffChars: number;
  runId?: string;
  messageId?: string;
  source?: string;
};

export type PreferenceExtractionResponse = {
  ok: boolean;
  applied?: boolean;
  skipped?: boolean;
  ignored?: string | null;
  reason?: string;
  source?: string;
  method?: string;
  diffChars?: number;
  keys?: string[];
  updates?: Partial<ClientPreferences>;
  candidateId?: string;
  durable?: boolean;
  approved?: boolean;
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'promoted' | 'rolled_back' | null;
  sessionOnly?: boolean;
  rawConfidence?: number;
  calibratedConfidence?: number;
  threshold?: number;
  explanation?: string;
  [key: string]: unknown;
};

export type FeedbackTextSyncResponse = {
  ok: boolean;
  applied?: boolean;
  ignored?: string | null;
  reason?: string | null;
  source?: string;
  updates?: Partial<ClientPreferences>;
  rawConfidence?: number;
  calibratedConfidence?: number;
  threshold?: number;
  method?: string;
  keys?: string[];
  explanation?: string;
  candidateId?: string;
  durable?: boolean;
  approved?: boolean;
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'promoted' | 'rolled_back' | null;
  sessionOnly?: boolean;
  memoryUpdated?: number;
  memoryMissing?: number;
  [key: string]: unknown;
};

function estimateChangedChars(beforeText: string, afterText: string): number {
  const before = beforeText || '';
  const after = afterText || '';
  if (before === after) return 0;

  const maxPrefix = Math.min(before.length, after.length);
  let prefix = 0;
  while (prefix < maxPrefix && before.charCodeAt(prefix) === after.charCodeAt(prefix)) {
    prefix += 1;
  }

  const beforeRemain = before.length - prefix;
  const afterRemain = after.length - prefix;
  const maxSuffix = Math.min(beforeRemain, afterRemain);
  let suffix = 0;
  while (
    suffix < maxSuffix &&
    before.charCodeAt(before.length - 1 - suffix) === after.charCodeAt(after.length - 1 - suffix)
  ) {
    suffix += 1;
  }

  const changedBefore = Math.max(0, before.length - prefix - suffix);
  const changedAfter = Math.max(0, after.length - prefix - suffix);
  return changedBefore + changedAfter;
}

export function buildPreferenceDiffPayload(input: {
  beforeText: string;
  afterText: string;
  runId?: string;
  messageId?: string;
  source?: string;
  minDiffChars?: number;
}): PreferenceDiffPayload | null {
  const beforeText = (input.beforeText || '').trim();
  const afterText = (input.afterText || '').trim();
  if (!beforeText && !afterText) return null;

  const minDiffChars = Math.max(1, Number(input.minDiffChars || DEFAULT_PREFERENCE_DIFF_MIN_CHARS));
  const diffChars = estimateChangedChars(beforeText, afterText);
  if (diffChars < minDiffChars) return null;

  return {
    beforeText,
    afterText,
    diffChars,
    minDiffChars,
    runId: input.runId,
    messageId: input.messageId,
    source: input.source || 'edit_diff_extractor',
  };
}

export async function syncPreferenceExtractionToGraph(input: {
  clientId: string;
  payload: PreferenceDiffPayload;
  idToken?: string | null;
}): Promise<PreferenceExtractionResponse | null> {
  if (!input.clientId || !input.payload) return null;
  try {
    const response = await fetch('/api/graph/feedback/preferences/extract', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(input.idToken ? { Authorization: `Bearer ${input.idToken}` } : {}),
      },
      body: JSON.stringify({
        action: 'extract',
        clientId: input.clientId,
        source: input.payload.source || 'edit_diff_extractor',
        beforeText: input.payload.beforeText,
        afterText: input.payload.afterText,
        runId: input.payload.runId,
        messageId: input.payload.messageId,
      }),
    });
    const data = (await response.json().catch(() => ({}))) as PreferenceExtractionResponse;
    if (!response.ok) {
      console.warn('[learning] preference_extract_graph_sync_failed', {
        status: response.status,
        error: data?.error || data,
      });
      return null;
    }
    return data;
  } catch (error) {
    console.warn('[learning] preference_extract_graph_sync_error', (error as any)?.message || error);
    return null;
  }
}

export async function syncPreferenceExtractionFeedbackToGraph(input: {
  clientId: string;
  acceptedByUser: boolean;
  idToken?: string | null;
  source?: string;
  rawConfidence?: number;
  calibratedConfidence?: number;
  runId?: string;
  messageId?: string;
}): Promise<boolean> {
  if (!input.clientId) return false;
  try {
    const response = await fetch('/api/graph/feedback/preferences/extract', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(input.idToken ? { Authorization: `Bearer ${input.idToken}` } : {}),
      },
      body: JSON.stringify({
        action: 'feedback',
        clientId: input.clientId,
        source: input.source || 'edit_diff_extractor',
        acceptedByUser: input.acceptedByUser,
        rawConfidence: input.rawConfidence,
        calibratedConfidence: input.calibratedConfidence,
        runId: input.runId,
        messageId: input.messageId,
      }),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      console.warn('[learning] preference_extract_feedback_graph_sync_failed', {
        status: response.status,
        error: text,
      });
      return false;
    }
    return true;
  } catch (error) {
    console.warn('[learning] preference_extract_feedback_graph_sync_error', (error as any)?.message || error);
    return false;
  }
}

export async function syncFeedbackTextToGraph(input: {
  messageId: string;
  feedbackText: string;
  runId?: string;
  thumbs?: 'up' | 'down';
  memoryIds?: string[];
  idToken?: string | null;
}): Promise<FeedbackTextSyncResponse | null> {
  const messageId = (input.messageId || '').trim();
  const feedbackText = (input.feedbackText || '').trim();
  if (!messageId || !feedbackText) return null;
  try {
    const response = await fetch('/api/graph/feedback/text', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(input.idToken ? { Authorization: `Bearer ${input.idToken}` } : {}),
      },
      body: JSON.stringify({
        messageId,
        runId: input.runId,
        feedbackText,
        thumbs: input.thumbs,
        memoryIds: Array.isArray(input.memoryIds) ? input.memoryIds : [],
      }),
    });
    const data = (await response.json().catch(() => ({}))) as FeedbackTextSyncResponse;
    if (!response.ok) {
      console.warn('[learning] feedback_text_graph_sync_failed', {
        status: response.status,
        error: data?.error || data,
      });
      return null;
    }
    return data;
  } catch (error) {
    console.warn('[learning] feedback_text_graph_sync_error', (error as any)?.message || error);
    return null;
  }
}
